import { useEffect, useRef, useState } from "react";
import { Phone, X } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useUser } from "@/hooks/useUser";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { fetchVerificationStatus } from "@/lib/users-api";
import { PhoneVerification } from "@/components/verification/PhoneVerification";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { needsBookingPhoneVerification } from "@/lib/booking-phone-prompt";

/** Whole hours (1dp) since an ISO timestamp; null when unknown/invalid. */
function hoursSince(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.round(((Date.now() - t) / 3_600_000) * 10) / 10;
}

/**
 * Task #6524 — post-payment phone verification banner for renters.
 *
 * When `feature_phone_verify_post_payment` is ON, checkout no longer blocks
 * on phone verification; this banner nudges renters who paid without a
 * verified phone to complete it afterwards, right where they manage their
 * trips. Flag OFF ⇒ renders nothing (byte-identical pages).
 *
 * The banner fetches the renter's verification status itself (the pages it
 * mounts on don't otherwise carry `phone_verified`), and only appears when
 * the server explicitly reports `phone_verified === false`.
 */
export function PostPaymentPhoneBanner({
  surface,
  tripId,
  tripCreatedAt,
  className,
}: {
  surface: "trips_banner" | "trip_detail";
  tripId?: string | number | null;
  /** ISO timestamp of booking creation (≈ payment) — enriches analytics. */
  tripCreatedAt?: string | null;
  className?: string;
}) {
  const { t } = useAppTranslation();
  const { session, user, profile, linkedAccountPhoneVerified } = useUser();
  const flagOn = useFeatureFlag("feature_phone_verify_post_payment");
  const token = session?.access_token;

  const [phoneVerified, setPhoneVerified] = useState<boolean | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [justVerified, setJustVerified] = useState(false);
  const trackedShown = useRef(false);

  useEffect(() => {
    if (!flagOn || !token) return;
    let cancelled = false;
    fetchVerificationStatus(token)
      .then((ver) => {
        if (cancelled) return;
        setPhoneVerified(ver.phone_verified);
        setPhone(ver.phone);
      })
      .catch(() => {
        // Fail closed (no banner) — this is a nudge, not a gate.
        if (!cancelled) setPhoneVerified(null);
      });
    return () => { cancelled = true; };
  }, [flagOn, token]);

  const visible = flagOn && !dismissed && (needsBookingPhoneVerification({
    isGuest: user?.app_metadata?.checkout_guest === true,
    guestEmailConfirmed: profile?.guest_email_confirmed,
    bookingPhoneVerified: phoneVerified,
    linkedAccountPhoneVerified,
  }) || justVerified);

  useEffect(() => {
    if (visible && phoneVerified === false && !trackedShown.current) {
      trackedShown.current = true;
      track("phone_verify_post_payment_prompt_shown", { surface, trip_id: tripId ?? null });
    }
  }, [visible, phoneVerified, surface, tripId]);

  if (!visible) return null;

  if (justVerified) {
    return (
      <div
        data-testid="banner-phone-verified-success"
        className={cn("rounded-xl border border-brand/20 bg-brand-tint px-4 py-3 text-sm text-brand-deep", className)}
        role="status"
      >
        {t("post_payment_phone.verified_msg")}
      </div>
    );
  }

  return (
    <div
      data-testid="banner-phone-verify-post-payment"
      className={cn("rounded-xl border border-amber-200 bg-amber-50 p-4", className)}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-900">
          <Phone aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">{t("post_payment_phone.banner_title")}</p>
          <p className="mt-0.5 text-sm text-amber-900/80">{t("post_payment_phone.banner_body")}</p>
          {!open ? (
            <button
              type="button"
              data-testid="button-verify-phone-now"
              onClick={() => setOpen(true)}
              className="mt-2 inline-flex items-center rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-hover"
            >
              {t("post_payment_phone.banner_cta")}
            </button>
          ) : (
            <div className="mt-3 rounded-lg bg-white p-3">
              <PhoneVerification
                initialPhone={phone}
                autoFocus
                onVerified={() => {
                  track("phone_verified_post_booking", {
                    surface,
                    trip_id: tripId ?? null,
                    booking_id: tripId ?? null,
                    hours_since_payment: hoursSince(tripCreatedAt),
                  });
                  setJustVerified(true);
                  setPhoneVerified(true);
                }}
                onCancel={() => setOpen(false)}
                cancelLabel={t("common.cancel")}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label={t("post_payment_phone.dismiss")}
          data-testid="button-dismiss-phone-banner"
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded p-1 text-amber-900/60 transition hover:text-amber-900"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
